import axios from "axios";

/**
 * Stable profile-photo upload (GLE-8234 backend, GLE-8239 frontend).
 *
 * A new photo is written ONCE to a fresh, immutable, publicly readable key and
 * is then served from a plain URL that never expires. Three steps:
 *   1. POST /v1/profile/upload/v2 → a presigned PUT, the key and the headers
 *      that are part of the signature;
 *   2. PUT the file to that URL with exactly those headers (plain axios: the
 *      app API client would add an Authorization header S3 rejects);
 *   3. POST /v1/profile/upload/complete {key} → the photo becomes the user's.
 *
 * The API calls are injected so each app passes its own client. Each call is
 * treated as failed whether the app's interceptor REJECTS a failed request or
 * RESOLVES it as an error value, since both exist in the codebase today.
 */

/** Backend limits for /profile/upload/v2 (ep_user_profile_image.go). */
export const PROFILE_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const PROFILE_IMAGE_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
];

export interface ProfileImageUploadTicketI {
  upload_url: string;
  key: string;
  headers: Record<string, string>;
}

export interface ProfileImageUploadCompleteI {
  profile_image_url: string;
}

interface ResponseLikeI<T> {
  status?: number | null;
  data?: T | null;
}

export interface UploadStableProfileImageDepsI {
  requestUpload: (params: {
    content_type: string;
    content_size: number;
  }) => Promise<ResponseLikeI<ProfileImageUploadTicketI>>;
  completeUpload: (params: {
    key: string;
  }) => Promise<ResponseLikeI<ProfileImageUploadCompleteI>>;
  /** Injected for tests; defaults to a plain axios PUT. */
  put?: (
    url: string,
    file: File,
    headers: Record<string, string>
  ) => Promise<ResponseLikeI<unknown>>;
}

export type ProfileImageUploadStageT =
  | "invalid_file"
  | "request_failed"
  | "put_failed"
  | "complete_failed";

export class ProfileImageUploadError extends Error {
  readonly stage: ProfileImageUploadStageT;

  constructor(stage: ProfileImageUploadStageT) {
    super(`Profile image upload failed at ${stage}`);
    this.name = "ProfileImageUploadError";
    this.stage = stage;
  }
}

const SUPPORT_SUFFIX =
  "Please message us in the chat window on the bottom right so we can assist.";

/** User-facing copy for a failed upload (the legacy screens' wording). */
export const profileImageUploadErrorMessage = (error: unknown): string => {
  const stage =
    error instanceof ProfileImageUploadError ? error.stage : undefined;

  if (stage === "invalid_file") {
    return "Please choose a JPG, PNG or GIF image.";
  }

  if (stage === "request_failed") {
    return `Failed to get image upload URL. ${SUPPORT_SUFFIX}`;
  }

  return `Failed to upload image file. ${SUPPORT_SUFFIX}`;
};

const defaultPut: NonNullable<UploadStableProfileImageDepsI["put"]> = (
  url,
  file,
  headers
) => axios.put(url, file, { headers });

const settle = async <T>(
  call: () => Promise<ResponseLikeI<T>>
): Promise<ResponseLikeI<T> | null> => {
  try {
    const response = await call();

    return response && response.status === 200 ? response : null;
  } catch {
    return null;
  }
};

/**
 * Uploads `file` as the current user's profile photo and resolves with its
 * stable public URL. Rejects with a ProfileImageUploadError naming the stage
 * that failed; nothing is saved to the profile unless every stage succeeds.
 */
export async function uploadStableProfileImage(
  file: File,
  deps: UploadStableProfileImageDepsI
): Promise<string> {
  if (
    !PROFILE_IMAGE_CONTENT_TYPES.includes(file.type) ||
    file.size <= 0 ||
    file.size > PROFILE_IMAGE_MAX_BYTES
  ) {
    throw new ProfileImageUploadError("invalid_file");
  }

  const ticket = await settle(() =>
    deps.requestUpload({ content_type: file.type, content_size: file.size })
  );
  const upload = ticket?.data;

  if (!upload?.upload_url || !upload.key || !upload.headers) {
    throw new ProfileImageUploadError("request_failed");
  }

  const put = deps.put ?? defaultPut;
  const stored = await settle(() =>
    put(upload.upload_url, file, upload.headers)
  );

  if (!stored) {
    throw new ProfileImageUploadError("put_failed");
  }

  const completed = await settle(() =>
    deps.completeUpload({ key: upload.key })
  );

  if (!completed?.data?.profile_image_url) {
    throw new ProfileImageUploadError("complete_failed");
  }

  return completed.data.profile_image_url;
}
